import { useState, useEffect } from 'react'
import { supabase } from '../supabase'
import { localDateStr } from '../utils/date'
import { compressImage } from '../utils/image'
import useIsMobile from '../hooks/useIsMobile'

const MAX_PHOTOS = 4

const emptyForm = {
  out_date: '', as_id: '', as_summary: '', plate: '', customer: '', customer_contact: '',
  destination: '', driver_name: '', driver_contact: '', memo: '',
}

export default function Delivery({ profile }) {
  const [list, setList] = useState([])
  const [asList, setAsList] = useState([])
  const [showModal, setShowModal] = useState(false)
  const [showDetail, setShowDetail] = useState(null)
  const [search, setSearch] = useState('')
  const [statusFilter, setStatusFilter] = useState('all')
  const [form, setForm] = useState(emptyForm)
  const [photos, setPhotos] = useState([])      // File[]
  const [previews, setPreviews] = useState([])  // dataURL[]
  const [loading, setLoading] = useState(false)
  const isMobile = useIsMobile()

  useEffect(() => { fetchList(); fetchAsList() }, [])

  async function fetchList() {
    const { data, error } = await supabase.from('deliveries').select('*').order('created_at', { ascending: false })
    if (error) console.error('deliveries 조회:', error.message)
    if (data) setList(data)
  }

  async function fetchAsList() {
    const { data } = await supabase.from('as_records').select('*').order('created_at', { ascending: false }).limit(100)
    if (data) setAsList(data)
  }

  function openNewModal() {
    setForm({ ...emptyForm, out_date: localDateStr() })
    setPhotos([])
    setPreviews([])
    setShowModal(true)
  }

  /** AS 기록 선택 시 차량번호·고객·연락처·내역 자동 입력 */
  function selectAs(asId) {
    if (!asId) {
      setForm(f => ({ ...f, as_id: '', as_summary: '' }))
      return
    }
    const rec = asList.find(a => String(a.id) === asId)
    if (!rec) return
    const lastHistory = rec.history?.[rec.history.length - 1]?.content || rec.history?.[0]?.content || ''
    setForm(f => ({
      ...f,
      as_id: asId,
      plate: rec.plate || f.plate,
      customer: rec.customer || f.customer,
      customer_contact: rec.contact || f.customer_contact,
      as_summary: lastHistory,
    }))
  }

  async function handleFiles(e) {
    const files = [...e.target.files]
    e.target.value = ''
    if (!files.length) return
    const room = MAX_PHOTOS - photos.length
    if (room <= 0) { alert(`사진은 최대 ${MAX_PHOTOS}장까지 첨부할 수 있습니다`); return }
    const take = files.slice(0, room)
    const urls = await Promise.all(take.map(f => new Promise(res => {
      const r = new FileReader(); r.onload = ev => res(ev.target.result); r.readAsDataURL(f)
    })))
    setPhotos(prev => [...prev, ...take])
    setPreviews(prev => [...prev, ...urls])
  }

  function removePhotoAt(i) {
    setPhotos(prev => prev.filter((_, idx) => idx !== i))
    setPreviews(prev => prev.filter((_, idx) => idx !== i))
  }

  async function submitDelivery() {
    if (!form.customer || !form.driver_name) { alert('고객명과 탁송기사 성함을 입력하세요'); return }
    setLoading(true)

    // 사진 압축 후 업로드 (1280px · JPEG 60%)
    const photoUrls = []
    for (let i = 0; i < photos.length; i++) {
      let blob
      try {
        blob = await compressImage(photos[i])
      } catch (e) {
        alert(`사진 ${i + 1} 처리 실패: ${e.message}`)
        setLoading(false)
        return
      }
      const fileName = `delivery_${Date.now()}_${i}.jpg`
      const { data, error } = await supabase.storage.from('drawings').upload(fileName, blob, { contentType: 'image/jpeg' })
      if (error) {
        alert('사진 업로드 실패: ' + error.message)
        setLoading(false)
        return
      }
      const { data: urlData } = supabase.storage.from('drawings').getPublicUrl(data.path)
      photoUrls.push(urlData.publicUrl)
    }

    const { error } = await supabase.from('deliveries').insert({
      out_date: form.out_date || localDateStr(),
      plate: form.plate,
      customer: form.customer,
      customer_contact: form.customer_contact,
      as_id: form.as_id || null,
      as_summary: form.as_summary || '',
      destination: form.destination,
      driver_name: form.driver_name,
      driver_contact: form.driver_contact,
      memo: form.memo,
      photo_urls: photoUrls,
      status: 'out',
      created_by: profile.id,
      created_by_name: profile.name,
    })
    if (error) {
      alert('저장 실패: ' + error.message + '\n\nSupabase에 deliveries 테이블이 있는지 확인하세요 (supabase/sql/deliveries.sql 실행)')
      setLoading(false)
      return
    }
    setShowModal(false)
    fetchList()
    setLoading(false)
  }

  async function markDelivered(d) {
    if (!window.confirm(`${d.plate || d.customer} — 인도 완료 처리할까요?`)) return
    const { error } = await supabase.from('deliveries')
      .update({ status: 'delivered', delivered_at: new Date().toISOString() })
      .eq('id', d.id)
    if (error) { alert('처리 실패: ' + error.message); return }
    fetchList()
    const { data } = await supabase.from('deliveries').select('*').eq('id', d.id).single()
    if (data) setShowDetail(data)
  }

  async function deleteDelivery(id) {
    if (!window.confirm('이 출고 기록을 삭제할까요?')) return
    const { error } = await supabase.from('deliveries').delete().eq('id', id)
    if (error) { alert('삭제 실패: ' + error.message); return }
    setShowDetail(null)
    fetchList()
  }

  const filtered = list.filter(d => {
    if (statusFilter !== 'all' && d.status !== statusFilter) return false
    const q = search.toLowerCase()
    if (!q) return true
    return d.plate?.toLowerCase().includes(q) || d.customer?.includes(q) || d.driver_name?.includes(q)
  })

  const statusBadge = s => s === 'delivered'
    ? { label: '인도완료', color: '#2d5a3d', bg: '#e8f2eb' }
    : { label: '출고됨', color: '#c0601a', bg: '#fff3e0' }

  const inputStyle = { width:'100%', padding:'9px 12px', border:'1px solid #e5e3de', borderRadius:8, fontSize:13, fontFamily:'inherit', boxSizing:'border-box' }
  const labelStyle = { display:'block', fontSize:12, fontWeight:600, color:'#6b6860', marginBottom:6 }

  return (
    <div>
      {/* 헤더 */}
      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:20, flexWrap:'wrap', gap:10 }}>
        <div>
          <div style={{ fontSize:18, fontWeight:700, marginBottom:4 }}>출고 관리</div>
          <div style={{ fontSize:13, color:'#a09e99' }}>탁송 출고 · 기사 정보 · 인도 완료 기록</div>
        </div>
        <button onClick={openNewModal}
          style={{ padding:'8px 16px', borderRadius:8, border:'none', background:'#2d5a3d', color:'#fff', cursor:'pointer', fontSize:13, fontWeight:500 }}>
          + 출고 등록
        </button>
      </div>

      {/* 검색 + 필터 */}
      <div style={{ display:'flex', gap:8, marginBottom:20 }}>
        <input value={search} onChange={e => setSearch(e.target.value)}
          placeholder="차량번호, 고객, 기사 검색..."
          style={{ ...inputStyle, flex:1 }} />
        <select value={statusFilter} onChange={e => setStatusFilter(e.target.value)}
          style={{ padding:'9px 12px', border:'1px solid #e5e3de', borderRadius:8, fontSize:13, fontFamily:'inherit', cursor:'pointer' }}>
          <option value="all">전체</option>
          <option value="out">출고됨</option>
          <option value="delivered">인도완료</option>
        </select>
      </div>

      {/* 모바일: 카드 목록 */}
      {isMobile ? (
        filtered.length === 0 ? (
          <div style={{ background:'#fff', border:'1px solid #e5e3de', borderRadius:10, textAlign:'center', padding:40, color:'#a09e99', fontSize:13 }}>
            출고 기록이 없습니다
          </div>
        ) : (
          <div style={{ display:'flex', flexDirection:'column', gap:10 }}>
            {filtered.map(d => {
              const badge = statusBadge(d.status)
              return (
                <div key={d.id} onClick={() => setShowDetail(d)}
                  style={{ background:'#fff', border:'1px solid #e5e3de', borderRadius:10, padding:14, cursor:'pointer', boxShadow:'0 1px 3px rgba(0,0,0,0.06)' }}>
                  <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:8, gap:8 }}>
                    <div style={{ display:'flex', alignItems:'center', gap:8, minWidth:0 }}>
                      {d.plate && (
                        <span style={{ border:'2px solid #1a1916', borderRadius:5, padding:'2px 8px', fontSize:12, fontWeight:700, whiteSpace:'nowrap' }}>
                          {d.plate}
                        </span>
                      )}
                      <span style={{ fontSize:14, fontWeight:600, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{d.customer}</span>
                    </div>
                    <span style={{ background:badge.bg, color:badge.color, padding:'3px 9px', borderRadius:20, fontSize:11, fontWeight:500, whiteSpace:'nowrap', flexShrink:0 }}>
                      {badge.label}
                    </span>
                  </div>
                  <div style={{ fontSize:12, color:'#6b6860', marginBottom:3 }}>
                    {d.out_date}{d.destination ? ' · ' + d.destination : ''}
                  </div>
                  <div style={{ fontSize:12, color:'#6b6860' }}>
                    🚚 {d.driver_name}{d.driver_contact ? ' · ' + d.driver_contact : ''}
                  </div>
                </div>
              )
            })}
          </div>
        )
      ) : (
      <div style={{ background:'#fff', border:'1px solid #e5e3de', borderRadius:10, overflowX:'auto' }}>
        <table style={{ width:'100%', borderCollapse:'collapse' }}>
          <thead>
            <tr>
              {['출고일','차량번호','고객','도착지','탁송기사','연락처','상태',''].map((h, i) => (
                <th key={i} style={{ padding:'10px 14px', fontSize:11, fontWeight:600, color:'#a09e99', borderBottom:'1px solid #e5e3de', background:'#f9f8f6', textAlign:'left', whiteSpace:'nowrap' }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {filtered.length === 0 ? (
              <tr><td colSpan={8} style={{ textAlign:'center', padding:40, color:'#a09e99' }}>출고 기록이 없습니다</td></tr>
            ) : filtered.map(d => {
              const badge = statusBadge(d.status)
              return (
                <tr key={d.id} style={{ borderBottom:'1px solid #e5e3de' }}>
                  <td style={{ padding:'12px 14px', whiteSpace:'nowrap' }}>{d.out_date}</td>
                  <td style={{ padding:'12px 14px' }}>
                    {d.plate && (
                      <span style={{ background:'#fff', border:'2px solid #1a1916', borderRadius:5, padding:'2px 10px', fontSize:13, fontWeight:700, letterSpacing:'0.04em', whiteSpace:'nowrap' }}>
                        {d.plate}
                      </span>
                    )}
                  </td>
                  <td style={{ padding:'12px 14px', fontWeight:600 }}>{d.customer}</td>
                  <td style={{ padding:'12px 14px', fontSize:12, color:'#6b6860', maxWidth:160, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{d.destination}</td>
                  <td style={{ padding:'12px 14px' }}>{d.driver_name}</td>
                  <td style={{ padding:'12px 14px', fontSize:12, color:'#6b6860', whiteSpace:'nowrap' }}>{d.driver_contact}</td>
                  <td style={{ padding:'12px 14px' }}>
                    <span style={{ background:badge.bg, color:badge.color, padding:'3px 10px', borderRadius:20, fontSize:11, fontWeight:500, whiteSpace:'nowrap' }}>
                      {badge.label}
                    </span>
                  </td>
                  <td style={{ padding:'12px 14px' }}>
                    <button onClick={() => setShowDetail(d)}
                      style={{ padding:'4px 12px', border:'1px solid #e5e3de', borderRadius:6, background:'#fff', cursor:'pointer', fontSize:12, whiteSpace:'nowrap' }}>
                      상세
                    </button>
                  </td>
                </tr>
              )
            })}
          </tbody>
        </table>
      </div>
      )}

      {/* 등록 모달 */}
      {showModal && (
        <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.45)', zIndex:200, display:'flex', alignItems:'center', justifyContent:'center' }}
          onClick={e => e.target === e.currentTarget && setShowModal(false)}>
          <div style={{ background:'#fff', borderRadius:14, width:520, maxWidth:'95vw', maxHeight:'90vh', overflowY:'auto', boxShadow:'0 20px 60px rgba(0,0,0,0.15)' }}>
            <div style={{ padding:'20px 24px 16px', borderBottom:'1px solid #e5e3de', display:'flex', justifyContent:'space-between', alignItems:'center' }}>
              <span style={{ fontSize:15, fontWeight:700 }}>출고 등록</span>
              <button onClick={() => setShowModal(false)} style={{ border:'none', background:'#f9f8f6', borderRadius:6, width:28, height:28, cursor:'pointer', fontSize:16 }}>✕</button>
            </div>
            <div style={{ padding:'20px 24px' }}>
              {/* AS 연동 */}
              <div style={{ marginBottom:14 }}>
                <label style={labelStyle}>AS 기록에서 불러오기 (선택)</label>
                <select value={form.as_id} onChange={e => selectAs(e.target.value)}
                  style={{ ...inputStyle, cursor:'pointer' }}>
                  <option value="">직접 입력</option>
                  {asList.map(a => (
                    <option key={a.id} value={a.id}>{a.plate} · {a.customer} ({a.date})</option>
                  ))}
                </select>
              </div>
              <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:14, marginBottom:14 }}>
                <div>
                  <label style={labelStyle}>출고일</label>
                  <input type="date" value={form.out_date} onChange={e => setForm({...form, out_date:e.target.value})} style={inputStyle} />
                </div>
                <div>
                  <label style={labelStyle}>차량번호</label>
                  <input value={form.plate} onChange={e => setForm({...form, plate:e.target.value})} placeholder="예) 12가 3456" style={inputStyle} />
                </div>
              </div>
              <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:14, marginBottom:14 }}>
                <div>
                  <label style={labelStyle}>고객명 *</label>
                  <input value={form.customer} onChange={e => setForm({...form, customer:e.target.value})} placeholder="홍길동" style={inputStyle} />
                </div>
                <div>
                  <label style={labelStyle}>고객 연락처</label>
                  <input value={form.customer_contact} onChange={e => setForm({...form, customer_contact:e.target.value})} placeholder="010-0000-0000" style={inputStyle} />
                </div>
              </div>
              {form.as_summary ? (
                <div style={{ background:'#f9f8f6', border:'1px solid #e5e3de', borderRadius:8, padding:'10px 14px', marginBottom:14 }}>
                  <div style={{ fontSize:10, color:'#a09e99', marginBottom:3 }}>AS 내역</div>
                  <div style={{ fontSize:12, color:'#6b6860', lineHeight:1.5 }}>{form.as_summary}</div>
                </div>
              ) : null}
              <div style={{ marginBottom:14 }}>
                <label style={labelStyle}>도착지 주소</label>
                <input value={form.destination} onChange={e => setForm({...form, destination:e.target.value})} placeholder="예) 경기도 화성시 ..." style={inputStyle} />
              </div>
              <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:14, marginBottom:14 }}>
                <div>
                  <label style={labelStyle}>탁송기사 성함 *</label>
                  <input value={form.driver_name} onChange={e => setForm({...form, driver_name:e.target.value})} placeholder="기사님 성함" style={inputStyle} />
                </div>
                <div>
                  <label style={labelStyle}>탁송기사 연락처</label>
                  <input value={form.driver_contact} onChange={e => setForm({...form, driver_contact:e.target.value})} placeholder="010-0000-0000" style={inputStyle} />
                </div>
              </div>
              <div style={{ marginBottom:14 }}>
                <label style={labelStyle}>차량 상태 메모</label>
                <textarea value={form.memo} onChange={e => setForm({...form, memo:e.target.value})}
                  placeholder="출고 전 차량 외관 상태, 특이사항 등" rows={3}
                  style={{ ...inputStyle, resize:'vertical' }} />
              </div>
              {/* 사진 */}
              <div>
                <label style={labelStyle}>사진 (최대 {MAX_PHOTOS}장 · 자동 압축)</label>
                <input type="file" accept="image/*" multiple onChange={handleFiles}
                  style={{ fontSize:12, marginBottom:8 }} />
                {previews.length > 0 && (
                  <div style={{ display:'flex', gap:8, flexWrap:'wrap' }}>
                    {previews.map((src, i) => (
                      <div key={i} style={{ position:'relative' }}>
                        <img src={src} alt={`사진 ${i+1}`} style={{ width:90, height:68, objectFit:'cover', borderRadius:8, border:'1px solid #e5e3de' }} />
                        <button onClick={() => removePhotoAt(i)}
                          style={{ position:'absolute', top:-6, right:-6, width:20, height:20, borderRadius:'50%', border:'none', background:'#b83030', color:'#fff', fontSize:11, cursor:'pointer', lineHeight:1 }}>✕</button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
            <div style={{ padding:'16px 24px', borderTop:'1px solid #e5e3de', display:'flex', gap:10, justifyContent:'flex-end' }}>
              <button onClick={() => setShowModal(false)}
                style={{ padding:'8px 16px', border:'1px solid #e5e3de', borderRadius:8, background:'#fff', cursor:'pointer', fontSize:13 }}>취소</button>
              <button onClick={submitDelivery} disabled={loading}
                style={{ padding:'8px 16px', border:'none', borderRadius:8, background:'#2d5a3d', color:'#fff', cursor:'pointer', fontSize:13, fontWeight:500 }}>
                {loading ? '등록 중...' : '등록하기'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 상세 모달 */}
      {showDetail && (() => {
        const badge = statusBadge(showDetail.status)
        return (
          <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.45)', zIndex:200, display:'flex', alignItems:'center', justifyContent:'center' }}
            onClick={e => e.target === e.currentTarget && setShowDetail(null)}>
            <div style={{ background:'#fff', borderRadius:14, width:560, maxWidth:'95vw', maxHeight:'90vh', overflowY:'auto', boxShadow:'0 20px 60px rgba(0,0,0,0.15)' }}>
              <div style={{ padding:'20px 24px 16px', borderBottom:'1px solid #e5e3de', display:'flex', justifyContent:'space-between', alignItems:'center' }}>
                <div style={{ display:'flex', alignItems:'center', gap:10 }}>
                  <span style={{ fontSize:15, fontWeight:700 }}>{showDetail.plate ? showDetail.plate + ' · ' : ''}{showDetail.customer}</span>
                  <span style={{ background:badge.bg, color:badge.color, padding:'3px 10px', borderRadius:20, fontSize:11, fontWeight:500 }}>{badge.label}</span>
                </div>
                <button onClick={() => setShowDetail(null)} style={{ border:'none', background:'#f9f8f6', borderRadius:6, width:28, height:28, cursor:'pointer', fontSize:16 }}>✕</button>
              </div>
              <div style={{ padding:'20px 24px' }}>
                <div style={{ background:'#f9f8f6', borderRadius:8, padding:14, marginBottom:16, display:'grid', gridTemplateColumns:'1fr 1fr', gap:12 }}>
                  {[['출고일', showDetail.out_date],
                    ['도착지', showDetail.destination],
                    ['고객명', showDetail.customer],
                    ['고객 연락처', showDetail.customer_contact],
                    ['탁송기사', showDetail.driver_name],
                    ['기사 연락처', showDetail.driver_contact],
                    ['처리 직원', showDetail.created_by_name],
                    ['인도완료 시각', showDetail.delivered_at ? new Date(showDetail.delivered_at).toLocaleString('ko-KR') : '-'],
                  ].map(([k, v]) => (
                    <div key={k}>
                      <div style={{ fontSize:10, color:'#a09e99', marginBottom:3 }}>{k}</div>
                      <div style={{ fontSize:13, fontWeight: k === '고객명' || k === '탁송기사' ? 600 : 400 }}>{v || '-'}</div>
                    </div>
                  ))}
                </div>
                {showDetail.as_summary && (
                  <div style={{ marginBottom:16 }}>
                    <div style={{ fontSize:12, fontWeight:600, color:'#6b6860', marginBottom:6 }}>AS 내역</div>
                    <div style={{ fontSize:12, color:'#6b6860', background:'#f9f8f6', borderRadius:6, padding:'8px 10px', border:'1px solid #e5e3de', lineHeight:1.6 }}>
                      {showDetail.as_summary}
                    </div>
                  </div>
                )}
                {showDetail.memo && (
                  <div style={{ marginBottom:16 }}>
                    <div style={{ fontSize:12, fontWeight:600, color:'#6b6860', marginBottom:6 }}>차량 상태 메모</div>
                    <div style={{ fontSize:12, color:'#6b6860', background:'#f9f8f6', borderRadius:6, padding:'8px 10px', border:'1px solid #e5e3de', lineHeight:1.6 }}>
                      {showDetail.memo}
                    </div>
                  </div>
                )}
                {Array.isArray(showDetail.photo_urls) && showDetail.photo_urls.length > 0 && (
                  <div>
                    <div style={{ fontSize:12, fontWeight:600, color:'#6b6860', marginBottom:6 }}>사진</div>
                    <div style={{ display:'flex', gap:8, flexWrap:'wrap' }}>
                      {showDetail.photo_urls.map((url, i) => (
                        <a key={i} href={url} target="_blank" rel="noreferrer">
                          <img src={url} alt={`출고 사진 ${i+1}`} style={{ width:120, height:90, objectFit:'cover', borderRadius:8, border:'1px solid #e5e3de' }} />
                        </a>
                      ))}
                    </div>
                  </div>
                )}
              </div>
              <div style={{ padding:'16px 24px', borderTop:'1px solid #e5e3de', display:'flex', justifyContent:'space-between' }}>
                <button onClick={() => deleteDelivery(showDetail.id)}
                  style={{ padding:'8px 16px', border:'none', borderRadius:8, background:'#fdeaea', color:'#b83030', cursor:'pointer', fontSize:13, fontWeight:500 }}>
                  🗑️ 삭제
                </button>
                <div style={{ display:'flex', gap:10 }}>
                  <button onClick={() => setShowDetail(null)}
                    style={{ padding:'8px 16px', border:'1px solid #e5e3de', borderRadius:8, background:'#fff', cursor:'pointer', fontSize:13 }}>닫기</button>
                  {showDetail.status !== 'delivered' && (
                    <button onClick={() => markDelivered(showDetail)}
                      style={{ padding:'8px 16px', border:'none', borderRadius:8, background:'#2d5a3d', color:'#fff', cursor:'pointer', fontSize:13, fontWeight:500 }}>
                      ✓ 인도 완료
                    </button>
                  )}
                </div>
              </div>
            </div>
          </div>
        )
      })()}
    </div>
  )
}
